
import { defineComponent, ref, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'primevue/usetoast';
import { isEqual } from 'lodash-es';
import { AppState, Config } from '@/store';
import { useAuth } from '@/plugins/Auth';

export default defineComponent({
  setup() {
    const toast = useToast();
    const store = useStore<AppState>();
    const auth = useAuth();
    const config = ref<Config>();

    // The form should not update the state directly, so watch for state changes and create a local
    // copy on changes. This includes the initial default values, loading from local storage on page
    // load, any commits to the store even if nothing changed, and changes persisted in local
    // storage in other browser windows. As we're using `<keep-alive>` in App.vue, this will also
    // work when this view is not currently active, but only if it has been active before...
    watchEffect(() => {
      // Only report and invalidate any current token when this is not the initial load, and is
      // different from what is currently in the form, so: if changed in another browser window
      if (config.value && !isEqual(config.value, store.state.config)) {
        toast.add({
          severity: 'warn',
          summary: 'Configuration was updated from another browser window',
          detail:
            'If you want to use different settings in multiple browser windows, then use different browsers, or do not use Save after making the changes.',
        });
        auth.clearAuth();
      }
      config.value = { ...store.state.config };
    });

    const save = async () => {
      auth.clearAuth();
      // This will trigger the watchEffect in all browser windows
      store.commit('persistInStorage', config.value);
      toast.add({
        severity: 'info',
        summary: 'Saved new configuration',
        detail: 'But did not yet log in using that new configuration',
        life: 3000,
      });
    };

    const login = async () => {
      toast.add({
        severity: 'info',
        summary: 'Logging in',
        detail: config.value?.username,
        life: 3000,
      });

      store.commit('setConfig', config.value);
      await auth.login();
    };

    const getToken = async () => {
      store.commit('setConfig', config.value);
      const t = await auth.getToken();
      toast.add({ severity: 'info', summary: 'Token', detail: t });
    };

    return { config, save, login, getToken };
  },
});
